import { create } from 'zustand'
import { dbStorage } from '../lib/indexedDBStorage'
import {
  applyTextStructureCommand,
  createTextStructureLayer,
  getTextStructureLayerId,
} from '../lib/readerTextStructure'
import type {
  TextStructureCommand,
  TextStructureLayer,
} from '../types/readerTextStructureTypes'

interface ReaderTextStructureStoreState {
  layers: Map<string, TextStructureLayer>
  loadingLayerIds: Set<string>
  getLayer: (chapterId: string, sourceWitnessId: string) => TextStructureLayer | undefined
  loadLayer: (chapterId: string, sourceWitnessId: string) => Promise<TextStructureLayer>
  saveLayer: (layer: TextStructureLayer) => Promise<void>
  applyCommand: (params: {
    chapterId: string
    sourceWitnessId: string
    command: TextStructureCommand
    rowOrderById: ReadonlyMap<string, number>
  }) => Promise<TextStructureLayer>
}

export const useReaderTextStructureStore = create<ReaderTextStructureStoreState>((set, get) => ({
  layers: new Map(),
  loadingLayerIds: new Set(),

  getLayer: (chapterId, sourceWitnessId) => (
    get().layers.get(getTextStructureLayerId(chapterId, sourceWitnessId))
  ),

  loadLayer: async (chapterId, sourceWitnessId) => {
    const id = getTextStructureLayerId(chapterId, sourceWitnessId)
    const cached = get().layers.get(id)
    if (cached) {
      return cached
    }
    set(state => ({
      loadingLayerIds: new Set(state.loadingLayerIds).add(id),
    }))
    try {
      const persisted = await dbStorage.get('reader_text_structure_layers', id)
      const layer = persisted || createTextStructureLayer({ chapterId, sourceWitnessId })
      set(state => ({
        layers: new Map(state.layers).set(id, layer),
      }))
      return layer
    } finally {
      set(state => {
        const loadingLayerIds = new Set(state.loadingLayerIds)
        loadingLayerIds.delete(id)
        return { loadingLayerIds }
      })
    }
  },

  saveLayer: async layer => {
    await dbStorage.put('reader_text_structure_layers', layer)
    set(state => ({
      layers: new Map(state.layers).set(layer.id, layer),
    }))
  },

  applyCommand: async ({ chapterId, sourceWitnessId, command, rowOrderById }) => {
    const id = getTextStructureLayerId(chapterId, sourceWitnessId)
    const layer = get().layers.get(id) || await get().loadLayer(chapterId, sourceWitnessId)
    const updated = applyTextStructureCommand({ layer, command, rowOrderById })
    await get().saveLayer(updated)
    return updated
  },
}))
